import { useRef, useEffect, useState } from "react";
import { Play, Pause, X, Volume2 } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { media, img } from "../lib/api";

export default function GlobalPlayer() {
  const { track, isPlaying, setIsPlaying, closePlayer } = usePlayer();
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!track || !audioRef.current) return;
    audioRef.current.src = media(track.audio_file_id, track.audio_url);
    audioRef.current.load();
    if (isPlaying) audioRef.current.play().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.play().catch(() => {});
    else audioRef.current.pause();
  }, [isPlaying]);

  if (!track) return null;

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  };

  const seek = (e) => {
    const val = Number(e.target.value);
    audioRef.current.currentTime = (val / 100) * duration;
    setProgress(val);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[70] bg-[#0C1424]/98 backdrop-blur-md border-t border-[#0066FF]/30"
      data-testid="global-audio-player-bar"
    >
      <audio
        ref={audioRef}
        onTimeUpdate={(e) => {
          setCurrent(e.target.currentTime);
          setProgress((e.target.currentTime / (e.target.duration || 1)) * 100);
        }}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center gap-4">
        <img
          src={img(track.cover_file_id, track.cover_url)}
          alt={track.title}
          className="h-12 w-12 rounded-lg object-cover shrink-0"
        />
        <div className="min-w-0 hidden sm:block w-44">
          <div className="font-cond font-bold uppercase text-sm truncate">{track.title}</div>
          {track.guest && <div className="text-xs text-[#0066FF] font-mono2 truncate">with {track.guest}</div>}
        </div>

        <button
          onClick={() => setIsPlaying((p) => !p)}
          data-testid="global-player-play-pause"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0066FF] hover:bg-[#2B82FF] transition-colors shrink-0"
        >
          {isPlaying ? <Pause className="h-5 w-5 text-white fill-white" /> : <Play className="h-5 w-5 text-white fill-white ml-0.5" />}
        </button>

        <div className="flex-1 flex items-center gap-3">
          <span className="font-mono2 text-xs text-[#94A3B8] w-10 text-right">{fmt(current)}</span>
          <input
            type="range"
            min={0}
            max={100}
            value={progress || 0}
            onChange={seek}
            data-testid="global-player-seek-slider"
            className="flex-1 h-1 accent-[#0066FF] cursor-pointer"
          />
          <span className="font-mono2 text-xs text-[#94A3B8] w-10">{fmt(duration)}</span>
        </div>

        <Volume2 className="h-5 w-5 text-[#94A3B8] hidden md:block" />
        <button
          onClick={closePlayer}
          data-testid="global-player-close"
          className="text-[#94A3B8] hover:text-[#FF3B30] transition-colors shrink-0"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
